import React, { useRef, useState, useEffect } from 'react';
import './Features.css';
import { gsap } from 'gsap';

const featuresData = [
  {
    title: 'Real-Time Wildlife Monitoring',
    description: 'IROD identifies and tracks animals instantly, offering continuous insight into wildlife activity — critical for informed, proactive conservation.',
    // Place your image in the public folder, e.g. public/feature1.jpg
    // You can also use a subfolder: '/images/feature1.jpg' if you put it in public/images/
    image: './camera1.jpg', // <-- Put your image path here
  },
  {
    title: 'Threat & Poacher Detection',
    description: 'Our AI alerts rangers to poaching and intrusions in real time, turning remote landscapes into protected zones with intelligent surveillance.',
    image: './animal_counting.jpg', // <-- Put your image path here
  },
  {
    title: 'Built for the Wild',
    description: 'IROD works without internet, storing and syncing data when possible — perfect for Africa’s most remote, high-risk regions.',
    image: './Ai_cam.jpg', // <-- Put your image path here
  },
  {
    title: 'Conservation Intelligence',
    description: 'From animal health to ecosystem trends, IROD delivers visual, decision-ready insights that empower conservation teams and drive strategic funding.',
    image: './advanced.jpg', // <-- Put your image path here
  },
];

function mod(n, m) {
  return ((n % m) + m) % m;
}

export default function Features() {
  const [current, setCurrent] = useState(0);
  const [dragStartX, setDragStartX] = useState(null);
  const [dragDelta, setDragDelta] = useState(0);
  const sliderRef = useRef();
  const sectionRef = useRef();
  const total = featuresData.length;
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const goTo = (idx) => {
    setCurrent(mod(idx, total));
  };

  // Smooth scroll to this section with parallax effect
  useEffect(() => {
    const handleHashScroll = () => {
      if (window.location.hash === '#features') {
        const section = sectionRef.current;
        if (section) {
          const rect = section.getBoundingClientRect();
          const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
          const targetY = rect.top + scrollTop - 40; // offset for nav if needed
          // Parallax effect using GSAP (use window.scrollTo directly for compatibility)
          gsap.to({}, {
            duration: 1.2,
            ease: 'power2.inOut',
            onUpdate: function() {
              const progress = this.progress();
              const currentY = window.scrollY;
              const newY = currentY + (targetY - currentY) * progress;
              window.scrollTo(0, newY);
            }
          });
        }
      }
    };
    window.addEventListener('hashchange', handleHashScroll);
    // If already on #features on load
    if (window.location.hash === '#features') {
      setTimeout(handleHashScroll, 100);
    }
    return () => window.removeEventListener('hashchange', handleHashScroll);
  }, []);

  // Parallax effect on scroll
  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const windowH = window.innerHeight;
      // Parallax only when in view
      if (rect.top < windowH && rect.bottom > 0) {
        const progress = 1 - Math.min(Math.max(rect.top / windowH, 0), 1);
        // Subtle parallax: much smaller movement and no blur
        gsap.to(sliderRef.current, {
          y: -progress * 18, // was 80
          scale: 1 + progress * 0.01, // was 0.04
          filter: 'none',
          duration: 0.5,
          ease: 'power2.out',
        });
        gsap.to('.features-slider-container > div:first-child', {
          y: -progress * 8, // was 40
          opacity: 1 - progress * 0.08, // was 0.18
          duration: 0.5,
          ease: 'power2.out',
        });
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Drag handlers
  const onDragStart = (e) => {
    const clientX = e.type && e.type.startsWith('touch') ? e.touches[0].clientX : e.clientX;
    setDragStartX(clientX);
    setDragDelta(0);
    // Prevent image dragging
    if (e.type === 'mousedown') e.preventDefault();
  };
  const onDragMove = (e) => {
    if (dragStartX === null) return;
    const clientX = e.type && e.type.startsWith('touch') ? e.touches[0].clientX : e.clientX;
    setDragDelta(clientX - dragStartX);
  };
  const onDragEnd = () => {
    if (Math.abs(dragDelta) > 60) {
      if (dragDelta < 0) {
        goTo(current + 1);
      } else {
        goTo(current - 1);
      }
    }
    setDragStartX(null);
    setDragDelta(0);
  };

  // Infinite visible cards for smooth drag
  const getCardIndex = (offset) => mod(current + offset, total);

  return (
    <div id="features" ref={sectionRef} style={{ position: 'relative', minHeight: '100vh', zIndex: 1, padding: isMobile ? '0 0 32px 0' : '0' }}>
      <div className="features-gradient-bg-global" aria-hidden="true"></div>
      <div style={{
        width: '100%',
        textAlign: 'center',
        zIndex: 5,
        position: 'relative',
        marginBottom: '3.5rem',
        marginTop: '1.5rem',
      }}>
        <h2 style={{
          fontSize: isMobile ? '2rem' : '2.7rem',
          fontWeight: 900,
          letterSpacing: '0.01em',
          color: '#fff',
          textShadow: '0 2px 32px rgba(99,102,241,0.22), 0 1px 8px rgba(0,0,0,0.13)',
          margin: 0,
          lineHeight: 1.1,
          background: 'linear-gradient(90deg, #6366f1 10%, #0ea5e9 90%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          filter: 'drop-shadow(0 2px 16px #0ea5e9cc)',
          animation: 'awesomeTitlePulse 3.5s infinite alternate',
        }}>
          Core Features of IROD
        </h2>
        <p style={{
          fontSize: isMobile ? '1rem' : '1.25rem',
          color: '#b6c3e0',
          margin: '0.7rem 0 0 0',
          fontWeight: 500,
          textShadow: '0 1px 8px rgba(0,0,0,0.13)',
          letterSpacing: '0.01em',
          opacity: 0.92,
          maxWidth: 540,
          marginLeft: 'auto',
          marginRight: 'auto'
        }}>
          Discover how IROD uses AI, smart surveillance, and offline-ready tech to protect Africa’s wildlife. Built for rugged environments, IROD detects poachers, tracks animals, and delivers real-time insights—making it a scalable, high-impact solution for global conservation.
        </p>
      </div>
      <div className="features-slider-container" style={{ width: isMobile ? '100vw' : 'auto', overflow: isMobile ? 'hidden' : 'visible', margin: '0 auto', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <div style={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
          <button className="slider-btn left" onClick={() => goTo(current - 1)} style={{ display: isMobile ? 'block' : 'inline-block' }}>&lt;</button>
          <div
            className="features-slider draggable-slider"
            ref={sliderRef}
            onMouseDown={onDragStart}
            onMouseMove={onDragMove}
            onMouseUp={onDragEnd}
            onMouseLeave={onDragEnd}
            onTouchStart={onDragStart}
            onTouchMove={onDragMove}
            onTouchEnd={onDragEnd}
            style={{
              cursor: dragStartX !== null ? 'grabbing' : 'grab',
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'center',
              width: isMobile ? '100vw' : 'auto',
              minHeight: isMobile ? '340px' : '420px',
              position: 'relative',
              overflow: isMobile ? 'hidden' : 'visible',
              touchAction: 'pan-x',
            }}
          >
            {[ -1, 0, 1 ].map((offset) => {
              const idx = getCardIndex(offset);
              return (
                <div
                  className={`feature-card${offset === 0 ? ' active' : ''}`}
                  key={offset}
                  style={{
                    backgroundImage: `url(${featuresData[idx].image})`,
                    zIndex: offset === 0 ? 2 : 1,
                    transform: isMobile
                      ? `translateX(${(offset * 100) + (offset === 0 ? dragDelta / 3.5 : 0)}vw) scale(${offset === 0 ? 1 : 0.97})`
                      : `translateX(${(offset * 360) + (offset === 0 ? dragDelta : 0)}px) scale(${offset === 0 ? 1 : 0.97})`,
                    opacity: offset === 0 ? 1 : 0.6,
                    pointerEvents: offset === 0 ? 'auto' : 'none',
                    transition: dragStartX ? 'none' : 'transform 0.5s cubic-bezier(.4,2,.3,1), opacity 0.5s cubic-bezier(.4,2,.3,1)',
                    width: isMobile ? '90vw' : '340px',
                    minHeight: isMobile ? '320px' : '400px',
                    margin: isMobile ? '0 2vw' : '0 16px',
                    borderRadius: '1.2rem',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    boxShadow: '0 2px 24px 0 rgba(30,40,90,0.13)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-end',
                  }}
                >
                  <div className="feature-card-overlay" style={{ padding: isMobile ? '18px 12px' : '28px 20px', borderRadius: '1.2rem', background: 'rgba(10,17,40,0.82)' }}>
                    <h3 style={{ fontSize: isMobile ? '1.25rem' : '1.5rem', margin: 0 }}>{featuresData[idx].title}</h3>
                    <p style={{ fontSize: isMobile ? '1rem' : '1.1rem', margin: '0.5em 0 0 0' }}>{featuresData[idx].description}</p>
                  </div>
                </div>
              );
            })}
          </div>
          <button className="slider-btn right" onClick={() => goTo(current + 1)} style={{ display: isMobile ? 'block' : 'inline-block' }}>&gt;</button>
        </div>
        {/* Dots for mobile - now always at the bottom, centered */}
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '18px', gap: '8px', width: '100%' }}>
          {featuresData.map((_, i) => (
            <span
              key={i}
              style={{
                width: isMobile ? 10 : 12,
                height: isMobile ? 10 : 12,
                borderRadius: '50%',
                background: i === current ? '#6366f1' : '#b6c3e0',
                opacity: i === current ? 1 : 0.5,
                display: 'inline-block',
                transition: 'background 0.3s',
                cursor: 'pointer',
              }}
              onClick={() => goTo(i)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
